import React, { useEffect, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';
import { Button, FormControlLabel, Radio, TextField, Select } from '@mui/material';
import {
  CampaignDetailPageStep,
  campaignSectionFieldItem,
  CampaignStructureItem,
} from '../../../../types/staff-campaign-types';
import { toAbsoluteUrl, convertArrayToObject, fileUpload } from 'src/app/common/utils';
import { createBlobAsAgent, getBlobAsAgent } from 'src/app/common/network';
import { useDispatch } from 'react-redux';
import { MANDATORY_FIELD_ERROR_TEXT } from '../../../../../../common/constants';
import { get as lodashGet } from 'lodash';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  sectionTitle: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    paddingLeft: 20,
    paddingRight: 20,
    height: 50,
    marginBottom: 8,
    fontWeight: 600,
    alignItems: 'center',
  },
  attachmentContainer: {
    background: '#FFFFFF',
    // width: '300px',
    minHeight: 250,
    margin: 5,
    display: 'flex',
    flexDirection: 'column',
    padding: 15,
  },
  attachmentTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '5px',
    fontWeight: 600,
    height: '20%',
  },
  mandatory: {
    color: 'red',
  },
  binIcon: {
    width: 20,
    height: 20,
    margin: 10,
  },
  imageContainer: {
    // flex: 1,
    width: 300,
    border: '10px solid #F4F4F4',
    // justifyContent: 'center',
    // alignContent: 'center',
    margin: 10,
    borderRadius: 10,
    background: '#F4F4F4',
    marginRight: 10,
  },
  fileText: {
    textDecoration: 'underline',
    color: '#38527E',
    marginBottom: 8,
    padding: '5px',
  },
  uploadAreaOuterContainer: {
    borderRadius: '8px',
    background: '#F4F4F4',
    display: 'flex',
    flex: 1,
    height: 160,
    width: 280,
  },
  uploadArea: {
    border: '2px dashed #C1C1C1',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  uploadAreaError: {
    border: '2px dashed #f018a6',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  uploadButton: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 10,
    color: '#f018a6',
    paddingLeft: 14,
  },
  viewBtn: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 10,
    background: '#F4F4F4',
    justifyContent: 'space-between',
  },
  card: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    background: '#F4F4F4',
    justifyContent: 'flex-start',
    backgroundColor: '#fff',
  },
  imgPreview: { width: '100%', height: '120px', textAlign: 'center', borderRadius: 8 },
  filePreview: {
    width: '100%',
    height: '120px',
    textAlign: 'center',
    background: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    textOverflow: 'ellipsis',
    wordBreak: 'break-all',
  },
}));

interface AttachmentProps {
  campaignTypeStructureData: CampaignStructureItem;
  formDispatch: (data: any) => void;
  formState: any;
  sectionKey: string;
  errorState: any;
  disabledEdit?: boolean;
  validationHandler: any;
}

function getMimeType(file: File) {
  if (file.type) {
    return file.type;
  }
  const mime = {
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    dotx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    docm: 'application/vnd.ms-word.document.macroEnabled.12',
    dotm: 'application/vnd.ms-word.template.macroEnabled.12',
    xls: 'application/vnd.ms-excel',
    xlt: 'application/vnd.ms-excel',
    xla: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xltx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
    xltm: 'application/vnd.ms-excel.template.macroEnabled.12',
    xlam: 'application/vnd.ms-excel.addin.macroEnabled.12',
    xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  };
  type MimeTypes = keyof typeof mime;
  const fileName = file.name;
  const fileExtension = (fileName?.split('.')?.pop()?.toLowerCase() || '') as MimeTypes;
  return mime[fileExtension];
}

const Attachment: React.FC<AttachmentProps> = ({
  sectionKey,
  campaignTypeStructureData,
  formDispatch,
  formState,
  errorState,
  disabledEdit,
  validationHandler,
}) => {
  const { classes } = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const Translation = (id: string) => intl.formatMessage({ id });
  const [uploadAttachmentProgress, setUploadAttachmentProgress] = useState<number>();

  const attachmentData = campaignTypeStructureData.sections.filter((item) => item.key == sectionKey)[0];

  const uploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldId: string = e.target.getAttribute('id') ?? '';
    const arrFile: File[] = lodashGet(e, 'target.files', []) as File[];
    if (arrFile.length > 0) {
      let arrResource: unknown[] = [],
        arrResourceIds: unknown[] = [];
      for (let index = 0; index < arrFile.length; index++) {
        const file: File = arrFile[index];
        const mimeType = getMimeType(file);
        if (!mimeType) {
          return dispatch(
            appendAlertItem([
              {
                severity: AlertType.ERROR,
                title: Translation('app.button.submitError'),
                content: Translation('common.fileNotSupport'),
              },
            ]),
          );
        }
        try {
          const createBlobRes = await createBlobAsAgent(
            { mimeType, accessLevel: 'anonymous', module: 'agencyCampaign', originalFilename: file.name },
            dispatch,
          );
          await fileUpload(createBlobRes[0].url, file, setUploadAttachmentProgress);
          const blobDetail = await getBlobAsAgent({ resourceIds: createBlobRes[0].blobId }, dispatch);
          const result = blobDetail[0];
          if (result) {
            validationHandler.onDismissErrorHandler(`${sectionKey}_${fieldId}`, result);
            arrResource.push(result);
            arrResourceIds.push(result.blobId);
            // formDispatch({ type: "MODIFY_FIELD", payload: { section: sectionKey, field: fieldId, value: {resources:[result],resourceIds:[result.blobId] } } });
          }
        } catch (err) {
        } finally {
          setUploadAttachmentProgress(undefined);
        }
      }

      const source: any[] = lodashGet(formState, `${sectionKey}.${fieldId}.resources`, []);
      const sourceId: any[] = lodashGet(formState, `${sectionKey}.${fieldId}.resourceIds`, []);
      formDispatch({
        type: 'MODIFY_FIELD',
        payload: {
          section: sectionKey,
          field: fieldId,
          value: { resources: [...source, ...arrResource], resourceIds: [...sourceId, ...arrResourceIds] },
        },
      });
    }
  };

  // const removeFile = (fieldItem: string) => {
  //   console.log(fieldItem);
  //   formDispatch({ type: "MODIFY_FIELD", payload: { section: sectionKey, field: fieldItem, value: null } });
  // }

  const removeFile = (fieldId: string, index: number) => {
    const arrSource: any[] = lodashGet(formState, `${sectionKey}.${fieldId}.resources`, []);
    const arrSourceId: any[] = lodashGet(formState, `${sectionKey}.${fieldId}.resourceIds`, []);

    arrSource.splice(index, 1);
    arrSourceId.splice(index, 1);
    // trigger warning: 'This field is mandatory' when no attachment
    const value = arrSource.length > 0 ? { resources: arrSource, resourceIds: arrSourceId } : undefined;
    formDispatch({
      type: 'MODIFY_FIELD',
      payload: {
        section: sectionKey,
        field: fieldId,
        value: value,
      },
    });
  };

  const viewFile = (url: string) => {
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(
        `{ "action" : "loadurlwithtarget", "payload" : { "target" : "_blank", "url" : "${encodeURIComponent(url)}" } }`,
      );
    } else {
      window.open(url, '_blank');
    }
  };

  const _renderAttachmentSection = (fieldItem: campaignSectionFieldItem) => {
    let ruleObject = convertArrayToObject(fieldItem.rules, 'title');
    const source: any[] = lodashGet(formState, `${sectionKey}.${fieldItem.key}.resources`, []);

    const txtUpload =
      source.length > 0
        ? Translation('agencyCampaign.create.uploadMore')
        : Translation('agencyCampaign.create.clickToUpload');
    const uploadIconUrl =
      source.length > 0
        ? toAbsoluteUrl('/media/icon/upload_more.png')
        : toAbsoluteUrl('/media/icon/upload_attachment.png');
    return (
      <div className={classes.attachmentContainer} key={fieldItem.key}>
        <div className={classes.attachmentTitle}>
          {Translation(`agencyCampaign.field.${fieldItem.key}`)}
          {ruleObject.Mandatory && ruleObject.Mandatory.value ? <span className={classes.mandatory}>*</span> : null}
          {/* {!disabledEdit && (
            <div onClick={() => removeFile(fieldItem.key)}>
              <img src={toAbsoluteUrl('/media/icon/bin.png')} alt={'Select from list'} className={classes.binIcon} />
            </div>
          )} */}
        </div>
        <div className={classes.card}>
          {source.map((item: any, index: number) => {
            const filePreview =
              item.mimeType.indexOf('image') > -1 ? (
                <img src={item.url} alt="image" className={classes.imgPreview} />
              ) : (
                <div className={classes.filePreview}>
                  <span> {item.originalFilename}</span>
                </div>
              );

            return (
              <div className={classes.imageContainer} key={index}>
                {/* <img src={item.url} alt="image" style={{ width: '100%', height: '120px', textAlign: 'center' }} /> */}
                {filePreview}
                <div className={classes.viewBtn}>
                  <Button variant="outlined" color="inherit" size="small" onClick={() => viewFile(item.url)}>
                    {Translation('app.button.view')}
                  </Button>

                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    disabled={disabledEdit}
                    onClick={() => removeFile(fieldItem.key, index)}
                  >
                    {Translation('app.button.remove')}
                  </Button>
                </div>
              </div>
            );
          })}
          {!disabledEdit && (
            <div className={classes.imageContainer}>
              <div className={classes.uploadAreaOuterContainer} key={fieldItem.title}>
                <div
                  className={
                    formState.stepPopulated.includes(CampaignDetailPageStep.ATTACHMENT) &&
                    errorState.mandatory[`${sectionKey}_${fieldItem.key}`]
                      ? classes.uploadAreaError
                      : classes.uploadArea
                  }
                >
                  <input
                    id={`${fieldItem.key}`}
                    hidden
                    type="file"
                    accept="audio/*,video/*,image/*,application/*, application/pdf,.pdf,application/csv, .csv, .doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onClick={(e) => {
                      const element = e.target as HTMLInputElement;
                      element.value = '';
                    }}
                    onChange={uploadAttachment}
                  />

                  <div className={classes.uploadButton}>
                    <div onClick={() => document.getElementById(`${fieldItem.key}`)!.click()}>
                      <img
                        src={uploadIconUrl}
                        alt={'Upload attachment'}
                        style={{ width: 46, height: 46, margin: 10 }}
                      />
                    </div>
                    <div>{txtUpload}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {formState.stepPopulated.includes(CampaignDetailPageStep.ATTACHMENT) &&
          errorState.mandatory[`${sectionKey}_${fieldItem.key}`] && (
            <span className={classes.errorText}>{MANDATORY_FIELD_ERROR_TEXT}</span>
          )}
      </div>
    );
  };

  if (lodashGet(attachmentData, 'sectionFields', []).length < 1) {
    return <span style={{ padding: 10 }}>{Translation('agencyCampaign.create.noAttachmentRequired')}</span>;
  }

  return (
    <div className={classes.container}>
      <div className={classes.sectionTitle}>
        {`${Translation('agencyCampaign.create.file')} ( ${
          formState[sectionKey] ? Object.keys(formState[sectionKey]).length : 0
        } )`}
      </div>

      <div className={classes.container} style={{ background: '#fff' }}>
        {attachmentData.sectionFields.map((fieldItem) => {
          return _renderAttachmentSection(fieldItem);
        })}
      </div>
    </div>
  );
};

export default Attachment;
